import { ChangeEvent, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Password } from '@mui/icons-material'
import GppGoodOutlinedIcon from '@mui/icons-material/GppGoodOutlined'
import { Alert, Button, Grid, InputAdornment, Link } from '@mui/material'
import { Form, Formik, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import FormikTextField from '../../components/formik-input'
import IntroContainer from '../../components/intro-container'
import ScreenContainer from '../../components/screen'
import SubmitContainer from '../../components/submit-container'
import { ActivationLocationState, sanitizeId } from '../../providers/activation-provider'
import Api from '../../utils/api'
import { MESHIFYID_TOKEN, SIDEWALKID_TOKEN, VERIFY_FOLDER_ID, VERIFY_NODE_ID } from '../../utils/constants'
import { saveDeviceIds } from '../../utils/helpers'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'

interface ISensorActivationFormValues {
  meshifyId: string
  sidewalkId: string
}

const SensorActivation = () => {
  const sharedClasses = useSharedStyles
  const classes = useStyles

  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    // make sure previous folder id and node id are cleared from local storage when we come to this page
    localStorage.removeItem(VERIFY_FOLDER_ID)
    localStorage.removeItem(VERIFY_NODE_ID)
  }, [])

  saveDeviceIds()

  // Inputs should only allow alphanumeric characters and only the first 7 characters
  const meshifyId = sanitizeId(localStorage.getItem(MESHIFYID_TOKEN) || '', 'uppercase')
  const sidewalkId = sanitizeId(localStorage.getItem(SIDEWALKID_TOKEN) || '', 'lowercase')

  const initialValues = {
    meshifyId: location.state?.fromCompleteSetup ? '' : meshifyId,
    sidewalkId: location.state?.fromCompleteSetup ? '' : sidewalkId,
  }

  const schema = Yup.object().shape({
    meshifyId: Yup.string().required(),
    sidewalkId: Yup.string().required(),
  })

  const handleSubmit = async (values: ISensorActivationFormValues) => {
    const cleanActivationId = sanitizeId(values.sidewalkId, 'lowercase')
    const cleanMeshifyId = sanitizeId(values.meshifyId, 'uppercase')

    // Find node by meshifyId (last 7 characters of vanity name) and sidewalkId (last 7 characters of unique id)
    const res = await Api.post('/api/v2/devices/verify', {
      activationId: cleanActivationId,
      meshifyId: cleanMeshifyId,
    })

    // If verify call is successful, we can proceed to the next step, assuming it is not already activated
    if (res.ok) {
      const { deviceStatus, id, folderId } = res.body

      localStorage.setItem(VERIFY_FOLDER_ID, folderId)
      localStorage.setItem(VERIFY_NODE_ID, id)

      if (deviceStatus === 'Not Activated' || deviceStatus === 'Extra' || (id === 0 && folderId === 0)) {
        const state: ActivationLocationState = {
          nodeTypeName: 'leak5s',
          nodeId: id,
          folderId,
          meshifyId: cleanMeshifyId,
          sidewalkId: cleanActivationId,
        }
        navigate('/sensor-folder-assignment', {
          state,
        })
      } else {
        navigate(`/dashboard/${folderId}`)
      }
    } else {
      enqueueSnackbar('We were unable to validate your sensor. Please try again.', { variant: 'error' })
    }
  }

  return (
    <ScreenContainer headerProps={{ showLogo: true, showLogoMobile: true }}>
      <Grid container={true} sx={classes.outerContainer}>
        <Grid container={true} size={12} sx={classes.centeredRow}>
          <Alert severity="info">Scan the back of your sensor with a mobile device for quick activation.</Alert>

          <IntroContainer title="Let's start activating your Defender Slim" />

          <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
            {({ isValid, values, setFieldValue, isSubmitting }: FormikProps<ISensorActivationFormValues>) => (
              <Form>
                <Grid container={true}>
                  <Grid container={true} size={12} sx={classes.centeredRow}>
                    <FormikTextField
                      name="meshifyId"
                      label="Device ID"
                      placeholder="Device ID"
                      hideErrors={true}
                      value={values.meshifyId}
                      onKeyUp={(event) => {
                        event.preventDefault()
                        const newValue = sanitizeId((event as unknown as ChangeEvent<HTMLInputElement>).target.value, 'uppercase')
                        setFieldValue('meshifyId', newValue)
                      }}
                      className="fs-exclude"
                      sx={sharedClasses.input}
                      startAdornment={
                        <InputAdornment position="start">
                          <Password />
                        </InputAdornment>
                      }
                    />
                  </Grid>

                  <Grid container={true} size={12} sx={classes.centeredRow}>
                    <FormikTextField
                      name="sidewalkId"
                      label="Activation Code"
                      placeholder="Activation Code"
                      hideErrors={true}
                      value={values.sidewalkId}
                      onKeyUp={(event) => {
                        event.preventDefault()
                        const newValue = sanitizeId((event as unknown as ChangeEvent<HTMLInputElement>).target.value, 'lowercase')
                        setFieldValue('sidewalkId', newValue)
                      }}
                      className="fs-exclude"
                      sx={sharedClasses.input}
                      startAdornment={
                        <InputAdornment position="start">
                          <GppGoodOutlinedIcon />
                        </InputAdornment>
                      }
                    />
                  </Grid>

                  <SubmitContainer>
                    <Button variant="contained" type="submit" disabled={!isValid || isSubmitting} sx={sharedClasses.submitButton}>
                      Activate Sensor
                    </Button>

                    <Link href="https://resources.meshify.com/defender-slim/#sensor-codes" rel="noopener noreferrer" target="_blank" underline="hover">
                      How do I find my codes?
                    </Link>
                  </SubmitContainer>
                </Grid>
              </Form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </ScreenContainer>
  )
}

export default SensorActivation
